import { useMemo } from 'react';

import { useLagoon } from '../../../../../hooks/useLagoon';
import { Option } from '../../../../../models/ProviderSearch';
import { GroupedCheckboxFilter } from '../../../../CheckBoxGroup/GroupedCheckboxFilter';
import { buildBhGroupedAoeData } from './groupedAoe.utils';

interface Props {
  model: string;
  defaultValue?: string[];
  isDisabled?: boolean;
  locationForAnalytics?: string;
  onOptionChange?: (data: { [key: string]: Option[] }) => void;
  options?: Option[]; // api data
  onApply?: (data: string[]) => void;
  submitBtnName?: string;
  disableSubmitBtn?: boolean;
  showApplyButton?: boolean;
}

/**
 * GroupedAoeFilter - AOE-Specific Wrapper
 *
 * This is a wrapper around the generic GroupedCheckboxFilter component
 * that handles the AOE-specific data transformation (grouping by categories).
 *
 * For future grouped filters, you have two options:
 * 1. Create a similar wrapper that transforms your data and uses GroupedCheckboxFilter
 * 2. Use GroupedCheckboxFilter directly if your data is already grouped
 */
export const GroupedAoeFilter = ({
  model,
  defaultValue = [],
  isDisabled = false,
  locationForAnalytics,
  options,
  onOptionChange = () => {},
  onApply,
  submitBtnName = 'Apply',
  disableSubmitBtn = false,
  showApplyButton = false,
}: Props) => {
  const allConfigs = useLagoon('config');

  // AOE-specific: Transform flat options into grouped structure
  const groupedAoeData = useMemo(
    () => buildBhGroupedAoeData(options || [], allConfigs),
    [options, allConfigs]
  );

  // Add AOE-specific test ID prefix for backward compatibility with Playwright tests
  // This ensures existing tests using 'bh-aoe-{category}' continue to work
  const groupedDataWithTestIds = useMemo(
    () =>
      groupedAoeData.map((category) => ({
        ...category,
        customTestIdPrefix: 'bh-aoe', // Backward compatible with Playwright
      })),
    [groupedAoeData]
  );

  // Delegate rendering to the generic GroupedCheckboxFilter
  return (
    <GroupedCheckboxFilter
      defaultValue={defaultValue}
      disableSubmitBtn={disableSubmitBtn}
      groupedData={groupedDataWithTestIds}
      isDisabled={isDisabled}
      locationForAnalytics={locationForAnalytics}
      model={model}
      onApply={onApply}
      onOptionChange={onOptionChange}
      showApplyButton={showApplyButton}
      submitBtnName={submitBtnName}
    />
  );
};
